/**
 * Invited employee, last step: the module tour (design: ob-invitee.jsx
 * EmpTour — replaces the older EmpWelcome / EmpLanding). Welcome → one part
 * per module group the org actually has → "Where do you want to start?".
 *
 * Finishing clears `needs_onboarding_tour` BEFORE entering the shell, so the
 * shell's spotlight ProductTour (which keys on the same flag) does not also
 * fire, then opens the chosen module.
 *
 * The design's intro names the org's plan; the validate response carries no
 * plan, so the intro says how many modules the person has instead.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Hand, Rocket } from "lucide-react";
import { Btn, Ic, ObShell, finishOnboardingTour, obToast, type InvitationModule } from "../kit";
import { InviteeTopBar } from "./common";
import { tourGroupsFor, tourModulePath, type TourModuleDef } from "./tourModules";

export interface EmpTourProps {
  /** Org name as displayed; also fills module descriptions ("Everyone in {{org}}"). */
  orgName: string;
  /** Full name for the top-bar badge. */
  personName?: string | null;
  firstName: string;
  modules: ReadonlyArray<InvitationModule>;
  /** Navigate into the shell (after the tour flag is cleared). */
  onOpen: (path: string) => void;
}

export function EmpTour({ orgName, personName, firstName, modules, onOpen }: EmpTourProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => tourGroupsFor(modules), [modules]);
  const all = useMemo(() => groups.flatMap((g) => g.modules), [groups]);
  const steps: string[] = ["intro", ...groups.map((g) => g.id), ...(all.length ? ["start"] : [])];
  const [i, setI] = useState(0);
  const [start, setStart] = useState<string | undefined>(all[0]?.key);
  const [busy, setBusy] = useState(false);

  const k = steps[i];
  const g = groups.find((x) => x.id === k);
  const last = i === steps.length - 1;

  const groupName = (id: string, fallback: string) => t(`onboarding.invitee.tour.group.${id}`, fallback);
  const modName = (m: TourModuleDef) => t(`onboarding.invitee.tour.mod.${m.key}.name`, m.name);
  const modDesc = (m: TourModuleDef) =>
    t(`onboarding.invitee.tour.mod.${m.key}.desc`, { defaultValue: m.desc, org: orgName || "We-Co" });

  const chosen = all.find((m) => m.key === start);

  const finish = async () => {
    setBusy(true);
    try {
      await finishOnboardingTour();
    } catch {
      setBusy(false);
      obToast.error(t("onboarding.invitee.tour.finishFailed", "We couldn't save your progress. Try again."));
      return;
    }
    onOpen(chosen ? tourModulePath(chosen) : "/");
  };

  return (
    <ObShell>
      <InviteeTopBar orgName={orgName} personName={personName} />
      <main className="ob-wrap ob-anim" style={{ maxWidth: 640, marginTop: 24 }}>
        <ol className="inv-steps" aria-label={t("onboarding.invitee.tour.progress", "Tour progress")}>
          {steps.map((s, n) => {
            const gg = groups.find((x) => x.id === s);
            const label =
              s === "intro"
                ? t("onboarding.invitee.tour.welcomeStep", "Welcome")
                : s === "start"
                  ? t("onboarding.invitee.tour.startStep", "Start")
                  : groupName(gg!.id, gg!.name);
            return (
              <li key={s} className={n === i ? "on" : n < i ? "done" : ""}>
                <button type="button" onClick={() => setI(n)} aria-current={n === i ? "step" : undefined}>
                  {label}
                </button>
              </li>
            );
          })}
        </ol>
        <section
          className="ob-glass ob-anim"
          key={k}
          style={{ padding: "clamp(22px,5cqi,36px)", display: "flex", flexDirection: "column", gap: 14 }}
          data-screen-label={`Invitee tour · ${k}`}
        >
          {k === "intro" && (
            <>
              <span className="ob-tile" style={{ width: 52, height: 52, borderRadius: 16, fontSize: 26 }}>
                <Ic icon={Hand} />
              </span>
              <h1 className="ob-h2" style={{ fontSize: 26 }}>
                {firstName
                  ? t("onboarding.invitee.tour.welcome", { defaultValue: "Welcome, {{name}}.", name: firstName })
                  : t("onboarding.invitee.tour.welcomeNoName", "Welcome.")}
              </h1>
              <p className="ob-p">
                {all.length
                  ? t("onboarding.invitee.tour.intro", {
                      defaultValue: "You have {{count}} modules in {{org}}. This short tour shows what each one is for.",
                      count: all.length,
                      org: orgName || "We-Co",
                    })
                  : t("onboarding.invitee.tour.introEmpty", "Your account is ready. Your administrator hasn't turned on any modules for you yet.")}
              </p>
              {groups.length > 0 && (
                <div className="inv-sum">
                  {groups.map((x) => (
                    <div key={x.id}>
                      <span className="ob-sm" style={{ fontWeight: 600, color: "var(--ink)" }}>
                        {groupName(x.id, x.name)}
                      </span>
                      <span className="ob-sm">{x.modules.map(modName).join(" · ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {g && (
            <>
              <p className="ob-eye">
                {t("onboarding.invitee.tour.part", {
                  defaultValue: "Part {{n}} of {{total}}",
                  n: groups.indexOf(g) + 1,
                  total: groups.length,
                })}
              </p>
              <h1 className="ob-h2" style={{ fontSize: 26 }}>
                {groupName(g.id, g.name)}
              </h1>
              <ul className="inv-mods">
                {g.modules.map((m) => (
                  <li key={m.key}>
                    <span className="ob-tile" style={{ width: 44, height: 44, borderRadius: 14, fontSize: 22, flexShrink: 0 }}>
                      <Ic icon={m.icon} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontWeight: 700, color: "var(--ink)", fontSize: 15 }}>{modName(m)}</span>
                      <span className="ob-sm">{modDesc(m)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {k === "start" && (
            <>
              <span className="ob-tile" style={{ width: 52, height: 52, borderRadius: 16, fontSize: 26 }}>
                <Ic icon={Rocket} />
              </span>
              <h1 className="ob-h2" style={{ fontSize: 26 }}>
                {t("onboarding.invitee.tour.startTitle", "Where do you want to start?")}
              </h1>
              <p className="ob-p">{t("onboarding.invitee.tour.startBody", "You can switch modules any time from the sidebar.")}</p>
              <div className="inv-pick" role="radiogroup" aria-label={t("onboarding.invitee.tour.firstModule", "First module")}>
                {all.map((m) => (
                  <button key={m.key} type="button" role="radio" aria-checked={start === m.key} onClick={() => setStart(m.key)}>
                    <Ic icon={m.icon} size={18} />
                    {modName(m)}
                  </button>
                ))}
              </div>
            </>
          )}
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, paddingTop: 8 }}>
            {i > 0 && i < steps.length - 1 && (
              <Btn kind="ter" size="m" onClick={() => setI(steps.length - 1)}>
                {t("onboarding.invitee.tour.skip", "Skip tour")}
              </Btn>
            )}
            <span style={{ marginInlineEnd: "auto" }} />
            {i > 0 && (
              <Btn kind="sec" size="m" disabled={busy} onClick={() => setI(i - 1)}>
                {t("onboarding.invitee.tour.back", "Back")}
              </Btn>
            )}
            {!last ? (
              <Btn size="m" onClick={() => setI(i + 1)} iconEnd={ArrowRight} flipEnd>
                {t("onboarding.invitee.tour.next", "Next")}
              </Btn>
            ) : (
              <Btn size="m" busy={busy} iconEnd={ArrowRight} flipEnd onClick={() => void finish()}>
                {chosen
                  ? t("onboarding.invitee.tour.open", { defaultValue: "Open {{module}}", module: modName(chosen) })
                  : t("onboarding.invitee.tour.openSuite", "Open We-Co Suite")}
              </Btn>
            )}
          </div>
        </section>
      </main>
    </ObShell>
  );
}
